// This file is loaded via the templater on all applicable pages.

// Import polyfills
import "core-js/actual/object/group-by"; // needed for Safari 16
import "core-js/actual/object/has-own"; // needed for Safari 15

// stylesheets
import "./tailwind.css";
import "../scss/pyrat.scss";
import "../scss/popup.scss";
import "../scss/root_style.scss";

import * as ko from "knockout";
import * as _ from "lodash";

import "./knockout/components";
import "./knockout/extensions";
import "./lib/charts";
import "./lib/matrixCode";
import * as pyratBackend from "./backend";
import { client as BackendClient } from "./backend/v1/client.gen";
import { ApiError, handleBackendError } from "./backend/v1/utils";
import { AssertionError, assert } from "./lib/assert";
import { ExceptHook } from "./lib/excepthook";
import { translated } from "./lib/localize";
import * as utils from "./lib/utils";
import { notifications, pyratFrontend } from "./pyratFrontend";

declare global {
    // values declared in the legacy javascript world but used in typescript code base
    interface Window {
        baseUrl: string;
        pyratTranslation: { [id: string]: string };
    }

    // values declared in here to be used in legacy javascript
    interface Window {
        pyratFrontend: typeof pyratFrontend;
        pyratBackend: typeof pyratBackend;
    }
}

BackendClient.setConfig({
    baseUrl: window.top.baseUrl + "/backend/v1",
    auth: () => pyratFrontend.session.sessionId,
});
BackendClient.interceptors.error.use((error, response, request): ApiError => {
    return new ApiError(request, response, error);
});

// Initialize top window instances
// biome-ignore lint/suspicious/noDoubleEquals: Comparing windows like this is intentional
if (window == window.top) {
    // The DOMContentLoaded event fires when the initial HTML document has been completely
    // loaded and parsed, without waiting for stylesheets, images, and subframes to finish loading.
    window.addEventListener(
        "DOMContentLoaded",
        () => {
            // pass
        },
        false,
    );
}

// Extend the window object to make our functions for legacy javascript.
Object.assign(window, {
    // make libraries available globally
    ko,
    _,

    // export global functions
    utils,
    assert,
    AssertionError,
    translated,
});

// Add frontend functions only once.
if (!window.pyratFrontend) {
    Object.assign(window, {
        pyratFrontend,
        pyratBackend: {
            v1: {
                ...pyratBackend.v1,
                // add error handler to the backend client for legacy frontend code
                handleBackendError,
            },
        },
    });
}

// The DOMContentLoaded event fires when the initial HTML document has been completely
// loaded and parsed, without waiting for stylesheets, images, and subframes to finish loading.
window.addEventListener(
    "DOMContentLoaded",
    () => {
        // register exception tracker
        const exceptHook = new ExceptHook();
        exceptHook.registerOnErrorHandler();
        exceptHook.registerInteractionTracker();

        // register notification cleanup tracker
        // TODO: move this to the notifications constructor, once we remove all the frames
        notifications.registerNotificationMessageCleaner(window);
    },
    false,
);

// The load event is fired when the whole page has loaded, including all dependent resources
// such as stylesheets and images.
window.addEventListener(
    "load",
    () => {
        // On iPad, scale the whole page and all frames a bit down.
        if (navigator.userAgent.match(/iPad/i) != null) {
            document.querySelector("body").style.zoom = "80%";
        }
    },
    false,
);
